import { useState } from 'react';

interface Bid {
  id: string;
  title: string;
  client: string;
  amount: string;
  status: 'draft' | 'submitted' | 'accepted' | 'declined' | 'expired';
  submittedDate: string;
  expiresDate: string;
}

const MOCK_BIDS: Bid[] = [
  { id: '1', title: 'Corporate Brand Video Campaign', client: 'TechCorp Inc.', amount: '$8,500', status: 'submitted', submittedDate: '2026-02-20', expiresDate: '2026-03-20' },
  { id: '2', title: 'Wedding Full-Day Coverage', client: 'Amanda & Jason', amount: '$4,200', status: 'accepted', submittedDate: '2026-02-10', expiresDate: '2026-03-10' },
  { id: '3', title: 'Product Launch Promo', client: 'NovaBrand', amount: '$6,500', status: 'submitted', submittedDate: '2026-02-18', expiresDate: '2026-03-18' },
  { id: '4', title: 'Music Video Production', client: 'DJ Stellar', amount: '$3,800', status: 'declined', submittedDate: '2026-01-15', expiresDate: '2026-02-15' },
  { id: '5', title: 'Real Estate Virtual Tours (5 properties)', client: 'Elite Realty', amount: '$5,000', status: 'draft', submittedDate: '-', expiresDate: '-' },
  { id: '6', title: 'Fitness Brand Content Series', client: 'FlexFit Co.', amount: '$7,200', status: 'expired', submittedDate: '2025-12-01', expiresDate: '2026-01-01' },
];

function statusColor(status: Bid['status']): { bg: string; fg: string } {
  switch (status) {
    case 'draft': return { bg: 'rgba(139,143,163,0.15)', fg: 'var(--color-text-muted)' };
    case 'submitted': return { bg: 'rgba(59,130,246,0.15)', fg: 'var(--color-info)' };
    case 'accepted': return { bg: 'rgba(34,197,94,0.15)', fg: 'var(--color-success)' };
    case 'declined': return { bg: 'rgba(239,68,68,0.15)', fg: 'var(--color-danger)' };
    case 'expired': return { bg: 'rgba(245,158,11,0.15)', fg: 'var(--color-warning)' };
  }
}

export function BidsPage() {
  const [filter, setFilter] = useState<'all' | Bid['status']>('all');

  const filteredBids = MOCK_BIDS.filter(
    (bid) => filter === 'all' || bid.status === filter,
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>Bids</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            Manage proposals and bids for potential projects.
          </p>
        </div>
        <button
          style={{
            padding: '10px 20px',
            background: 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          + Create Bid
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {(['all', 'draft', 'submitted', 'accepted', 'declined', 'expired'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: filter === status ? 'var(--color-primary)' : 'transparent',
              color: filter === status ? '#fff' : 'var(--color-text-muted)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Bids list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filteredBids.map((bid) => {
          const sc = statusColor(bid.status);
          return (
            <div
              key={bid.id}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: '20px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                cursor: 'pointer',
              }}
            >
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '4px' }}>
                  {bid.title}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  {bid.client}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Submitted</div>
                  <div style={{ fontSize: '0.8125rem' }}>{bid.submittedDate}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Expires</div>
                  <div style={{ fontSize: '0.8125rem' }}>{bid.expiresDate}</div>
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, minWidth: '80px', textAlign: 'right' }}>
                  {bid.amount}
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    background: sc.bg,
                    color: sc.fg,
                    minWidth: '80px',
                    textAlign: 'center',
                  }}
                >
                  {bid.status}
                </span>
              </div>
            </div>
          );
        })}

        {filteredBids.length === 0 && (
          <div
            style={{
              padding: '48px',
              textAlign: 'center',
              color: 'var(--color-text-muted)',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            No bids match the selected filter.
          </div>
        )}
      </div>
    </div>
  );
}
